// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
const sprintf = require('sprintf-js').sprintf;
import Button from 'presentations/views/components/atoms/Button';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Icon from 'presentations/views/components/atoms/Icon';
import Avatar, {
  Group as AvatarGroup,
} from 'presentations/views/components/atoms/Avatar';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  primary,
  white,
  gray03,
  black,
  gray05,
  gray01,
  gray04,
  gray06,
  gray02,
} from 'presentations/views/styles/color';
import { DateTime, formatToTime } from 'infras/datetime';
import { Trail } from 'presentations/views/components/atoms/Transition';
import Link from 'presentations/views/components/atoms/Link';
import { SharedGuestUserItem } from 'presentations/api/schema/user';
import { helpPath } from 'presentations/routes/path';
import { UnavailableInfomation } from 'presentations/api/schema/calendar_event';
import { CalendarUnavailableReasonType } from 'presentations/api/schema/calendar_event/enum';
import { Transparency } from 'domains/models/event/transparency';

interface Props {
  isLoading?: boolean;
  selectedDate: DateTime | null;
  selectedDatetime: DateTime | null;
  timeList: Array<(any | null)[]>;
  timeZone: string;
  color: string;
  isLightColor: boolean;
  isFullHeight?: boolean;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode?: boolean;
  onSelect?: any;
  onClickBlockTime?: Function;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  let isSelectedDate: boolean = false;
  if (props.selectedDate && props.selectedDatetime) {
    isSelectedDate = props.selectedDate.hasSame(props.selectedDatetime, 'day');
  }
  const selectedTimeString: string | null = props.selectedDatetime
    ? formatToTime(props.selectedDatetime)
    : null;

  return (
    <TimeList isFullHeight={props.isFullHeight}>
      <Trail isOpen={true}>
        {props.isLoading &&
          new Array(10).fill(0).map((_, index) => (
            <SkeletonItem
              key={index}
              isRevealGuestUser={props.isRevealGuestUser}
            >
              <Skeleton width="100%" height="100%" />
            </SkeletonItem>
          ))}
        {!props.isLoading &&
          props.timeList.map((item, index) => {
            // utcで持ち回ってきた時刻を、ここで表示上のみtimezoneを考慮する
            // const dateTime = getDateTime({ hour: item[0], minute: item[1] });
            const time = `${sprintf('%d', item[0])}:${sprintf(
              '%02d',
              item[1],
              // dateTime.minute,
            )}`;
            const isSelectedDateTime =
              isSelectedDate && time === selectedTimeString;
            const isFullCapacity = item[3] === 0;
            const isShowCapacity = item.length >= 4 && Number(item[3]) > 0;
            const viewerEventName = item[2] ? item[2].name || null : null;
            const viewerEventColor = item[2] ? item[2].color || null : null;
            const guestUsers: SharedGuestUserItem[] | null =
              props.isRevealGuestUser && item && item.length >= 5
                ? item[4]
                : null;

            // block理由周りをitemから取得する
            const unavailableInfomations: UnavailableInfomation[] =
              item && item.length >= 6 ? item[5] : [];
            const uniqueUnavailableInfomations: UnavailableInfomation[] = [];
            if (unavailableInfomations) {
              for (const reason of unavailableInfomations) {
                if (
                  uniqueUnavailableInfomations.find(
                    (uui) => uui.reason === reason.reason,
                  )
                ) {
                  continue;
                }
                uniqueUnavailableInfomations.push(reason);
              }
            }
            const hasUnavailableInfomation =
              uniqueUnavailableInfomations.length > 0;
            const isTransparentEvent =
              item[6] && item[6] === Transparency.Transparent;

            if (props.isHostDiagnosticMode && hasUnavailableInfomation) {
              return (
                <TimeBlockListItem key={`${time}_${index}`}>
                  <TimeButton
                    color="inherit"
                    variant="text"
                    onClick={() =>
                      props.onClickBlockTime &&
                      props.onClickBlockTime(time, uniqueUnavailableInfomations)
                    }
                  >
                    <TimeText isRevealGuestUser={false}>
                      {time}
                      <BlockEventText>
                        <BlockReasonIcon
                          name="information-outline"
                          color="inherit"
                        />
                        <BlockReasonList>
                          {uniqueUnavailableInfomations.map(
                            (info: any, i: number) => (
                              <BlockReason key={i}>
                                {t(`block reason ${info.reason}`)}
                              </BlockReason>
                            ),
                          )}
                        </BlockReasonList>
                      </BlockEventText>
                    </TimeText>
                  </TimeButton>
                  <LaunchIcon name="launch" color="inherit" />
                </TimeBlockListItem>
              );
            } else if (
              props.isHostDiagnosticMode &&
              viewerEventName &&
              isTransparentEvent
            ) {
              return (
                <TimeListItem
                  key={`${time}_${index}`}
                  color={props.color}
                  isLightColor={props.isLightColor}
                  isRevealGuestUser={props.isRevealGuestUser}
                >
                  <TimeButton
                    color="inherit"
                    variant="text"
                    onClick={() =>
                      props.onClickBlockTime &&
                      props.onClickBlockTime(time, [
                        {
                          reason: CalendarUnavailableReasonType.Transparent,
                          users: [],
                          eventName: viewerEventName,
                        },
                      ])
                    }
                  >
                    <TimeText isRevealGuestUser={props.isRevealGuestUser}>
                      {time}
                      <BlockEventText>
                        <BlockReasonIcon name="information-outline" />
                        <EventText isRevealGuestUser={props.isRevealGuestUser}>
                          {viewerEventName}
                          <ExistNoSchedule>
                            {t('no schedule setting')}
                          </ExistNoSchedule>
                        </EventText>
                      </BlockEventText>
                    </TimeText>
                  </TimeButton>
                  <LaunchIcon name="launch" />
                </TimeListItem>
              );
            } else if (
              viewerEventName &&
              props.isShowViewerCalendar &&
              !isFullCapacity
            ) {
              return (
                <TimeEventListItem
                  key={`${time}_${index}`}
                  isSelected={isSelectedDateTime}
                  color={props.color}
                  isLightColor={props.isLightColor}
                  isRevealGuestUser={props.isRevealGuestUser}
                >
                  <TimeButton
                    color="inherit"
                    variant="text"
                    onClick={() => props.onSelect(time, undefined, guestUsers)}
                  >
                    <TimeText isRevealGuestUser={props.isRevealGuestUser}>
                      {time}
                      <EventText isRevealGuestUser={props.isRevealGuestUser}>
                        <StyledDot color={viewerEventColor} />
                        {viewerEventName}
                      </EventText>
                    </TimeText>
                    {guestUsers && guestUsers.length > 0 && (
                      <StyledAvatarGroup max={3} spacing={4}>
                        {guestUsers.map((user) => (
                          <StyledAvatar
                            src={user.profileImage}
                            color={user.color}
                          >
                            {user.name.charAt(0)}
                          </StyledAvatar>
                        ))}
                      </StyledAvatarGroup>
                    )}
                  </TimeButton>
                  <ChevronIcon
                    name="chevron-right"
                    color="inherit"
                    isLightColor={false}
                    iconColor="inherit"
                    size={fontSize(24)}
                    isVisible={true}
                  />
                </TimeEventListItem>
              );
            } else if (isFullCapacity) {
              return (
                <TimeFullListItem key={`${time}_${index}`} color={'gray03'}>
                  <TimeText isRevealGuestUser={props.isRevealGuestUser}>
                    {time}
                    <EventText isRevealGuestUser={props.isRevealGuestUser}>
                      <CapacityFull>{t('full capacity')}</CapacityFull>
                    </EventText>
                  </TimeText>
                  <ChevronIcon
                    name="chevron-right"
                    color="inherit"
                    iconColor={props.color}
                    isLightColor={props.isLightColor}
                    size={fontSize(24)}
                    isVisible={false}
                  />
                </TimeFullListItem>
              );
            } else {
              return (
                <TimeListItem
                  key={`${time}_${index}`}
                  isSelected={isSelectedDateTime}
                  color={props.color}
                  isLightColor={props.isLightColor}
                  isRevealGuestUser={props.isRevealGuestUser}
                >
                  <TimeButton
                    color="inherit"
                    variant="text"
                    onClick={() => props.onSelect(time, undefined, guestUsers)}
                  >
                    <TimeText isRevealGuestUser={props.isRevealGuestUser}>
                      {time}
                      {isShowCapacity && (
                        <CapacityText
                          isRevealGuestUser={props.isRevealGuestUser}
                        >
                          {t('remaining capacity', {
                            count: item[3],
                          })}
                        </CapacityText>
                      )}
                    </TimeText>
                    {guestUsers && guestUsers.length > 0 && (
                      <StyledAvatarGroup max={3} spacing={4}>
                        {guestUsers.map((user) => (
                          <StyledAvatar
                            src={user.profileImage}
                            color={user.color}
                          >
                            {user.name.charAt(0)}
                          </StyledAvatar>
                        ))}
                      </StyledAvatarGroup>
                    )}
                  </TimeButton>
                  <ChevronIcon
                    name="chevron-right"
                    color="inherit"
                    iconColor={props.color}
                    isLightColor={props.isLightColor}
                    size={fontSize(24)}
                    isVisible={true}
                  />
                </TimeListItem>
              );
            }
          })}
        {!props.isLoading &&
          props.isHostDiagnosticMode &&
          props.timeList.length === 0 && (
            <NoTimeList>
              {t('No items.0')}
              <Link href={helpPath('7155127')} target="_blank">
                {t('No items.1')}
                <Icon
                  name="checkbox-multiple-blank-outline"
                  size={fontSize(14)}
                />
              </Link>
            </NoTimeList>
          )}
      </Trail>
    </TimeList>
  );
});

export default Component;

const TimeList = styled.div<{ isFullHeight?: boolean }>`
  ${({ isFullHeight }) =>
    isFullHeight
      ? css`
          height: 100%};
          ${mediaMax.smartphone} {
            height: 100%;
          }
        `
      : css`
          overflow: scroll;
          height: 248px;
          ${mediaMax.smartphone} {
            height: 259px;
          }
        `}
`;
const SkeletonItem = styled.div<{
  isRevealGuestUser: boolean;
}>`
  margin-bottom: ${spacing(8)};

  ${({ isRevealGuestUser }) => css`
    height: ${isRevealGuestUser ? '82px' : '63px'};
  `}
`;
const TimeListItem = styled.div<{
  isSelected?: boolean;
  color: string;
  isLightColor: boolean;
  isRevealGuestUser: boolean;
}>`
  border-radius: ${borderRadius()};
  margin-bottom: ${spacing(8)};
  text-align: center;
  :last-of-type {
    /* margin-bottom: 0; */
  }
  font-weight: ${fontWeight('bold')};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ isRevealGuestUser }) => css`
    height: ${isRevealGuestUser ? '82px' : '63px'};
  `}

  ${(props) =>
    css`
      border: 1px solid ${props.isLightColor ? gray04(props) : props.color};
      background-color: ${props.isSelected ? props.color : white(props)};
      color: ${props.isSelected
        ? props.isLightColor
          ? black(props)
          : white(props)
        : props.isLightColor
        ? gray01(props)
        : props.color};
    `}

  :hover {
    background-color: ${({ color }) => color};
    color: ${({ isLightColor }) => (isLightColor ? black : white)};
  }
  .MuiButton-root:hover {
    background-color: initial;
  }
  position: relative;
`;
const TimeEventListItem = styled(TimeListItem)`
  ${(props) =>
    !props.isSelected &&
    css`
      background-color: ${gray05(props)};
      border: 1px solid ${gray03(props)};
      color: ${black(props)};
    `}
`;

const TimeFullListItem = styled.div`
  border-radius: ${borderRadius()};
  margin-bottom: ${spacing(8)};
  text-align: center;
  font-weight: ${fontWeight('bold')};

  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 63px;
  background-color: ${gray06};
  color: ${gray03};

  .MuiButton-root:hover {
    background-color: initial;
  }
`;

const TimeButton = styled(Button)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  .MuiButton-label {
    flex-direction: column;
  }
`;

const TimeText = styled.div<{
  isRevealGuestUser: boolean;
}>`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(18)};
  overflow: hidden;

  ${({ isRevealGuestUser }) =>
    isRevealGuestUser &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
    `}
`;
const EventText = styled.div<{
  isRevealGuestUser: boolean;
}>`
  font-size: ${fontSize(12)};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  ${({ isRevealGuestUser }) =>
    isRevealGuestUser &&
    css`
      margin-left: ${spacing(12)};
    `}
`;
const StyledDot = styled.div<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  margin-right: ${spacing(8)};
`;
const ExistNoSchedule = styled.span`
  padding-left: ${spacing(8)};
  border-left: 1px solid ${gray03};
  margin-left: ${spacing(8)};
`;
const CapacityText = styled.div<{
  isRevealGuestUser: boolean;
}>`
  font-size: ${fontSize(12)};
  font-weight: initial;
  line-height: 1em;

  ${({ isRevealGuestUser }) =>
    isRevealGuestUser &&
    css`
      margin-left: ${spacing(12)};
    `}
`;
const CapacityFull = styled.span`
  color: ${gray01};
`;
const ChevronIcon = styled(Icon)<{
  iconColor: string;
  isLightColor: boolean;
  isVisible: boolean;
}>`
  position: absolute;
  right: ${spacing(8)};
  fill: ${({ iconColor, isLightColor }) => (isLightColor ? gray01 : iconColor)};
  ${(props) => css`
    visibility: ${props.isVisible ? 'visible' : 'hidden'};
  `};
`;

const StyledAvatarGroup = styled(AvatarGroup)`
  justify-content: center;
  align-items: center;
  margin-left: ${spacing(8)};

  .MuiAvatar-colorDefault {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 20px;
  height: 20px;
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}
`;

const NoTimeList = styled.div`
  max-width: 240px;
  margin: 0 auto;
`;

const TimeBlockListItem = styled.div`
  border-radius: ${borderRadius()};
  margin-bottom: ${spacing(8)};
  text-align: center;
  font-weight: ${fontWeight('bold')};

  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 63px;
  border: 1px dashed ${gray02};
  background-color: ${gray06};
  color: ${gray01};

  .MuiButton-root:hover {
    background-color: initial;
  }
  position: relative;
`;
const BlockEventText = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(12)};
`;
const BlockReasonIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  margin-right: ${spacing(4)};
  flex-shrink: 0;

  ${mediaMax.smartphone} {
    width: 16px !important;
    height: 16px !important;
  }
`;
const BlockReasonList = styled.div``;
const BlockReason = styled.span`
  padding-right: ${spacing(8)};
  border-right: 1px solid ${gray03};
  margin-right: ${spacing(8)};

  :last-of-type {
    padding-right: 0;
    border-right: none;
    margin-right: 0;
  }
`;
const LaunchIcon = styled(Icon)`
  margin-right: ${spacing(16)};
  fill: ${gray03};
  width: 20px !important;
  height: 20px !important;
  position: absolute;
  right: ${spacing(8)};

  ${mediaMax.smartphone} {
    width: 16px !important;
    height: 16px !important;
  }
`;
